import { useMutation, useQuery } from '@apollo/client';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { IconButton } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DetailItem } from 'components/common/detail-item';
import { ConfirmModal } from 'components/common/modals';
import { PageLoading } from 'components/views/page-loading';
import { format } from 'date-fns';
import { useSnackbar } from 'providers/snackbar';
import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { LeadList, LeadListVariables } from 'types/apollo/LeadList';
import {
  LeadListsRemoveUnpostedLeadsUpdate,
  LeadListsRemoveUnpostedLeadsUpdateVariables,
} from 'types/apollo/LeadListsRemoveUnpostedLeadsUpdate';
import { LeadListQuery } from './lead-list.graphql';
import { LeadListsRemoveUnpostedLeadsUpdateMutation } from './leads-remove-unposted-update.graphql';

export const CampaignLeadList: FC = () => {
  const { id, leadListId } = useParams();
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();

  const { data, loading } = useQuery<LeadList, LeadListVariables>(LeadListQuery, {
    variables: {
      leadListId: leadListId || '',
    },
  });

  const [removeUnpostedLeadsConfirm, setRemoveUnpostedLeadsConfirm] = useState(false);

  const [leadListsRemoveUnpostedLeadsUpdate, leadListsRemoveUnpostedLeadsUpdateMutation] =
    useMutation<LeadListsRemoveUnpostedLeadsUpdate, LeadListsRemoveUnpostedLeadsUpdateVariables>(
      LeadListsRemoveUnpostedLeadsUpdateMutation,
    );

  const onRemoveUnpostedLeadsConfirm = async () => {
    if (!id || !leadListId) return;
    setRemoveUnpostedLeadsConfirm(false);
    try {
      await leadListsRemoveUnpostedLeadsUpdate({
        variables: {
          input: {
            leadListId: leadListId,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [LeadListQuery],
      });
      openSnackbar({
        message: 'Remove unposted leads executed successfully',
        severity: 'success',
      });
    } catch (err) {
      // caught in GraphQLWrapper
    }
  };

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          p: {
            xs: 2,
            md: 3,
          },
        }}
      >
        <Stack spacing={4}>
          <Card variant="outlined" sx={{ padding: '16px' }}>
            <Stack spacing={2}>
              <Stack direction="row" sx={{ alignItems: 'center' }} spacing={2}>
                <IconButton size="small" onClick={() => navigate(-1)}>
                  <ChevronLeftIcon fontSize="large" />
                </IconButton>
                <Typography variant="h5">Details</Typography>
              </Stack>

              {loading && <PageLoading />}

              {!loading && !!data?.leadList && (
                <>
                  <DetailItem label="File Name" value={data.leadList.originalFilename} />
                  <DetailItem label="Lead Count" value={data.leadList.leadCount ?? 0} />
                  <DetailItem label="Rejected" value={data.leadList.rejectedLeadsCount ?? 0} />
                  <DetailItem label="Flagged" value={data.leadList.flaggedLeadsCount ?? 0} />
                  <DetailItem
                    label="Sent to Campaign"
                    value={
                      data.leadList.sentToCampaignTimestamp
                        ? format(
                            new Date(data.leadList.sentToCampaignTimestamp),
                            'yyyy-MM-dd hh:mm',
                          )
                        : 'Not sent'
                    }
                  />
                  <DetailItem label="Status" value={data.leadList.status} />
                  <DetailItem
                    label="Created At"
                    value={format(new Date(data.leadList.createdAt), 'yyyy-MM-dd hh:mm')}
                  />
                </>
              )}
            </Stack>
          </Card>

          {!loading && !!data?.leadList && (
            <Card variant="outlined" sx={{ padding: '16px' }}>
              <Stack spacing={2}>
                <Typography variant="h5">Remove unposted Leads</Typography>

                {!data.leadList.removedUnpostedLeadCount && (
                  <>
                    <Typography variant="body1">
                      Removing unposted leads will clear all the leads that have not posted,
                      specific to this lead list.
                    </Typography>
                    <Button
                      variant="outlined"
                      disabled={leadListsRemoveUnpostedLeadsUpdateMutation.loading}
                      color="error"
                      onClick={() => setRemoveUnpostedLeadsConfirm(true)}
                    >
                      Remove Unposted Leads
                    </Button>
                  </>
                )}
                {!!data.leadList.removedUnpostedLeadCount && (
                  <Typography variant="body1">
                    {data.leadList.removedUnpostedLeadCount} were removed from this list.
                  </Typography>
                )}
              </Stack>
            </Card>
          )}
        </Stack>
      </Container>
      <ConfirmModal
        open={removeUnpostedLeadsConfirm}
        onCancel={() => setRemoveUnpostedLeadsConfirm(false)}
        onConfirm={onRemoveUnpostedLeadsConfirm}
      />
    </>
  );
};
